/**
 * Follow-Up Engine Context
 *
 * Admin-configurable disposition rules, message templates, and
 * follow-up sequences — the single source of truth that the
 * runtime follow-up logic reads from.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import type { ExecutionMethod, FollowUpPriority } from "./unifiedFollowUp";
import { useDeferHeavyProvidersForCurrentRoute } from "@/lib/routeScope";
import { FollowUpEngineService } from "@/services/follow-up-engine.service";

/* ═══════ TYPES ═══════ */

export interface DispositionRule {
  id: string;
  dispositionName: string;
  defaultFollowUpDays: number;
  executionMethod: ExecutionMethod;
  priorityLevel: FollowUpPriority;
  active: boolean;
  escalationDays?: number;
  managerNotify: boolean;
  autoSendThankYou: boolean;
}

export interface MessageTemplate {
  id: string;
  dispositionRuleId: string;
  type: "thank_you" | "follow_up_1" | "follow_up_2" | "follow_up_3" | "final";
  label: string;
  body: string;
  autoSend: boolean;
}

export interface SequenceStep {
  id: string;
  dispositionRuleId: string;
  order: number;
  dayOffset: number;
  actionType: ExecutionMethod;
  templateId: string;
  autoSend: boolean;
  label: string;
}

export interface FollowUpEngineConfig {
  rules: DispositionRule[];
  templates: MessageTemplate[];
  sequences: SequenceStep[];
  globalSettings: {
    thankYouDelayMinutes: number;
    escalationEnabled: boolean;
    aiSuggestionsEnabled: boolean;
  };
}

/* ═══════ CONTEXT ═══════ */

interface FollowUpEngineContextType {
  config: FollowUpEngineConfig;
  addRule: (rule: Omit<DispositionRule, "id">) => void;
  updateRule: (id: string, updates: Partial<DispositionRule>) => void;
  deleteRule: (id: string) => void;
  toggleRule: (id: string) => void;
  addTemplate: (template: Omit<MessageTemplate, "id">) => void;
  updateTemplate: (id: string, updates: Partial<MessageTemplate>) => void;
  deleteTemplate: (id: string) => void;
  addSequenceStep: (step: Omit<SequenceStep, "id">) => void;
  updateSequenceStep: (id: string, updates: Partial<SequenceStep>) => void;
  deleteSequenceStep: (id: string) => void;
  updateGlobalSettings: (updates: Partial<FollowUpEngineConfig["globalSettings"]>) => void;
  getRuleByDisposition: (name: string) => DispositionRule | undefined;
  getTemplatesForRule: (ruleId: string) => MessageTemplate[];
  getSequenceForRule: (ruleId: string) => SequenceStep[];
  resolveTemplate: (templateBody: string, vars: Record<string, string>) => string;
}

const FollowUpEngineContext = createContext<FollowUpEngineContextType | null>(null);

export function FollowUpEngineProvider({ children }: { children: React.ReactNode }) {
  const [rules, setRules] = useState<DispositionRule[]>([]);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [sequences, setSequences] = useState<SequenceStep[]>([]);
  const [globalSettings, setGlobalSettings] = useState<FollowUpEngineConfig["globalSettings"]>({
    thankYouDelayMinutes: 30,
    escalationEnabled: true,
    aiSuggestionsEnabled: false,
  });
  const deferHeavyProvider = useDeferHeavyProvidersForCurrentRoute();

  useEffect(() => {
    if (deferHeavyProvider) return;

    FollowUpEngineService.loadConfig().then((saved) => {
      if (!saved) return;
      setRules(saved.rules);
      setTemplates(saved.templates);
      setSequences(saved.sequences);
      if (saved.globalSettings) setGlobalSettings(saved.globalSettings);
    });
  }, [deferHeavyProvider]);

  const addRule = useCallback((rule: Omit<DispositionRule, "id">) => {
    const newRule: DispositionRule = { ...rule, id: `rule-${Date.now()}` };
    setRules((prev) => [...prev, newRule]);
    FollowUpEngineService.upsertRule(newRule);
  }, []);

  const updateRule = useCallback((id: string, updates: Partial<DispositionRule>) => {
    setRules((prev) => {
      const next = prev.map((r) => (r.id === id ? { ...r, ...updates } : r));
      const updated = next.find((r) => r.id === id);
      if (updated) FollowUpEngineService.upsertRule(updated);
      return next;
    });
  }, []);

  const deleteRule = useCallback((id: string) => {
    setRules((prev) => prev.filter((r) => r.id !== id));
    setTemplates((prev) => prev.filter((t) => t.dispositionRuleId !== id));
    setSequences((prev) => prev.filter((s) => s.dispositionRuleId !== id));
    FollowUpEngineService.deleteRule(id);
  }, []);

  const toggleRule = useCallback((id: string) => {
    setRules((prev) => {
      const next = prev.map((r) => (r.id === id ? { ...r, active: !r.active } : r));
      const updated = next.find((r) => r.id === id);
      if (updated) FollowUpEngineService.upsertRule(updated);
      return next;
    });
  }, []);

  const addTemplate = useCallback((tpl: Omit<MessageTemplate, "id">) => {
    const newTpl: MessageTemplate = { ...tpl, id: `tpl-${Date.now()}` };
    setTemplates((prev) => [...prev, newTpl]);
    FollowUpEngineService.upsertTemplate(newTpl);
  }, []);

  const updateTemplate = useCallback((id: string, updates: Partial<MessageTemplate>) => {
    setTemplates((prev) => {
      const next = prev.map((t) => (t.id === id ? { ...t, ...updates } : t));
      const updated = next.find((t) => t.id === id);
      if (updated) FollowUpEngineService.upsertTemplate(updated);
      return next;
    });
  }, []);

  const deleteTemplate = useCallback((id: string) => {
    setTemplates((prev) => prev.filter((t) => t.id !== id));
    FollowUpEngineService.deleteTemplate(id);
  }, []);

  const addSequenceStep = useCallback((step: Omit<SequenceStep, "id">) => {
    const newStep: SequenceStep = { ...step, id: `seq-${Date.now()}` };
    setSequences((prev) => [...prev, newStep]);
    FollowUpEngineService.upsertSequenceStep(newStep);
  }, []);

  const updateSequenceStep = useCallback((id: string, updates: Partial<SequenceStep>) => {
    setSequences((prev) => {
      const next = prev.map((s) => (s.id === id ? { ...s, ...updates } : s));
      const updated = next.find((s) => s.id === id);
      if (updated) FollowUpEngineService.upsertSequenceStep(updated);
      return next;
    });
  }, []);

  const deleteSequenceStep = useCallback((id: string) => {
    setSequences((prev) => prev.filter((s) => s.id !== id));
    FollowUpEngineService.deleteSequenceStep(id);
  }, []);

  const updateGlobalSettings = useCallback((updates: Partial<FollowUpEngineConfig["globalSettings"]>) => {
    setGlobalSettings((prev) => {
      const next = { ...prev, ...updates };
      FollowUpEngineService.saveGlobalSettings(next);
      return next;
    });
  }, []);

  const getRuleByDisposition = useCallback((name: string) => {
    return rules.find((r) => r.dispositionName === name && r.active);
  }, [rules]);

  const getTemplatesForRule = useCallback((ruleId: string) => {
    return templates.filter((t) => t.dispositionRuleId === ruleId);
  }, [templates]);

  const getSequenceForRule = useCallback((ruleId: string) => {
    return sequences.filter((s) => s.dispositionRuleId === ruleId).sort((a, b) => a.order - b.order);
  }, [sequences]);

  const resolveTemplate = useCallback((body: string, vars: Record<string, string>) => {
    let resolved = body;
    Object.entries(vars).forEach(([key, val]) => {
      resolved = resolved.replace(new RegExp(`{{${key}}}`, "g"), val);
    });
    return resolved;
  }, []);

  const config: FollowUpEngineConfig = { rules, templates, sequences, globalSettings };

  return (
    <FollowUpEngineContext.Provider
      value={{
        config,
        addRule,
        updateRule,
        deleteRule,
        toggleRule,
        addTemplate,
        updateTemplate,
        deleteTemplate,
        addSequenceStep,
        updateSequenceStep,
        deleteSequenceStep,
        updateGlobalSettings,
        getRuleByDisposition,
        getTemplatesForRule,
        getSequenceForRule,
        resolveTemplate,
      }}
    >
      {children}
    </FollowUpEngineContext.Provider>
  );
}

export function useFollowUpEngine() {
  const ctx = useContext(FollowUpEngineContext);
  if (!ctx) throw new Error("useFollowUpEngine must be used within FollowUpEngineProvider");
  return ctx;
}
